import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { SHARED_TAGS } from '../slices/constants'
import { setTargetGroupInfo } from '../slices/targetGroupSlice'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'
import { usersApi } from './usersApi'

export const targetGroupsApi = createApi({
  reducerPath: 'targetGroupApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: [
    'TargetGroupAvailableCodes',
    'TargetGroupDetails',
    'TargetGroupUpdateCards',
    'AllTargetGroups',
    'UsersInTargetGroup',
    'TargetGroupMsbFailureLogs',
    'TargetGroupActionBoxes',
    'TargetGroupFailureLogs',
    SHARED_TAGS.USERS
  ],
  endpoints: (builder) => ({
    getTargetGroups: builder.query({
      query: (requestParams: GetTargetGroupParam) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getTargetGroupsByTenantID',
          requestParams
        ),
      providesTags: ['AllTargetGroups']
    }),
    getAllSegmentList: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getAllSegmentList',
          requestParams
        )
    }),
    getTargetGroupDetailsByID: builder.query({
      query: (requestParams: GetTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getTargetGroupDetailsByID',
          requestParams
        ),
      providesTags: ['TargetGroupDetails'],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = (await queryFulfilled) as {
            data?: GetTargetGroupDetails
          }
          if (data?.result) {
            dispatch(setTargetGroupInfo(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    getTargetGroupUpdateCardsByID: builder.query({
      query: (requestParams: { TargetGroupID: string }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getTargetGroupAllUpdateCards',
          requestParams
        ),
      providesTags: ['TargetGroupUpdateCards']
    }),
    getTargetGroupActionBoxesByID: builder.query({
      query: (requestParams: { TargetGroupID: string }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getTargetGroupAllActionBoxes',
          requestParams
        ),
      providesTags: ['TargetGroupActionBoxes']
    }),
    getTargetGroupAvailableCodes: builder.query({
      query: (requestParams: GetGroupAvailableCodesParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'searchCodes',
          requestParams
        ),
      providesTags: ['TargetGroupAvailableCodes']
    }),
    deleteTargetGroupAvailableCode: builder.mutation({
      query: (requestParams: { PromotionCode: string }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'deleteInviteCode',
          requestParams
        ),
      invalidatesTags: ['TargetGroupAvailableCodes', 'TargetGroupDetails']
    }),
    getExcludedCampaignsFromGroup: builder.query({
      query: (requestParams: { TargetGroupID: string }) =>
        generateRPCQuery(
          apiConstants.CAMPAIGNS_RPC_PATH,
          'getExcludedCampaignsFromGroup',
          requestParams
        )
    }),
    getAllOffersFromGroup: builder.query({
      query: (requestParams: { TargetGroupID: string }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getAllOffersFromTargetGroup',
          requestParams
        )
    }),
    getUsersInSegmentCount: builder.query({
      query: (requestParams: { TargetGroupID: string }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getUsersInSegmentCount',
          requestParams
        ),
      providesTags: ['UsersInTargetGroup']
    }),
    validateManualTargetGroup: builder.query({
      query: (requestParams: ManualTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'validateManualTargetGroup',
          requestParams
        )
    }),
    createManualTargetGroup: builder.mutation({
      query: (requestParams: ManualTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'createManualTargetGroup',
          requestParams
        ),
      invalidatesTags: ['AllTargetGroups', 'TargetGroupFailureLogs']
    }),
    updateTargetGroupAndDeleteUsers: builder.mutation({
      query: (requestParams: ManualTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'updateTargetGroupAndDeleteUsers',
          requestParams
        ),
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(usersApi.util.invalidateTags([SHARED_TAGS.USERS]))
      },
      invalidatesTags: [
        'TargetGroupDetails',
        'TargetGroupUpdateCards',
        'AllTargetGroups',
        'UsersInTargetGroup',
        'TargetGroupMsbFailureLogs',
        'TargetGroupActionBoxes',
        'TargetGroupFailureLogs'
      ]
    }),
    updateTargetGroupName: builder.mutation({
      query: (requestParams: ManualTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'updateTargetGroupName',
          requestParams
        ),
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(usersApi.util.invalidateTags([SHARED_TAGS.USERS]))
      },
      invalidatesTags: [
        'TargetGroupDetails',
        'TargetGroupUpdateCards',
        'AllTargetGroups',
        'UsersInTargetGroup',
        'TargetGroupMsbFailureLogs',
        'TargetGroupActionBoxes',
        'TargetGroupFailureLogs'
      ]
    }),
    updateTargetGroupAndAddUsers: builder.mutation({
      query: (requestParams: ManualTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'updateTargetGroupAndAddUsers',
          requestParams
        ),
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(usersApi.util.invalidateTags([SHARED_TAGS.USERS]))
      },
      invalidatesTags: [
        'TargetGroupDetails',
        'TargetGroupUpdateCards',
        'AllTargetGroups',
        'UsersInTargetGroup',
        'TargetGroupMsbFailureLogs',
        'TargetGroupActionBoxes',
        'TargetGroupFailureLogs'
      ]
    }),
    updateTargetGroupAndReplaceUsers: builder.mutation({
      query: (requestParams: ManualTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'updateTargetGroupAndReplaceUsers',
          requestParams
        ),
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(usersApi.util.invalidateTags([SHARED_TAGS.USERS]))
      },
      invalidatesTags: [
        'TargetGroupDetails',
        'TargetGroupUpdateCards',
        'AllTargetGroups',
        'UsersInTargetGroup',
        'TargetGroupMsbFailureLogs',
        'TargetGroupActionBoxes',
        'TargetGroupFailureLogs'
      ]
    }),
    deleteTargetGroup: builder.mutation({
      query: (requestParams: DeleteTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'removeTargetGroup',
          requestParams
        ),
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(usersApi.util.invalidateTags([SHARED_TAGS.USERS]))
      },
      invalidatesTags: [
        'TargetGroupDetails',
        'TargetGroupUpdateCards',
        'AllTargetGroups',
        'UsersInTargetGroup',
        'TargetGroupMsbFailureLogs',
        'TargetGroupActionBoxes',
        'TargetGroupFailureLogs'
      ]
    }),
    downloadUsersFromTargetGroup: builder.mutation({
      query: (requestParams: CommonDownloadFromTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'downloadUsersFromTargetGroup',
          requestParams
        )
    }),
    downloadAvailableCodesFromTargetGroup: builder.mutation({
      query: (requestParams: CommonDownloadFromTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'downloadAvailableCodesFromTargetGroup',
          requestParams
        )
    }),
    validateInviteCodeSegmentSteps: builder.mutation({
      query: (requestParams: InviteCodeSegmentValidationParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'validateInviteCodeSegmentSteps',
          requestParams
        )
    }),
    preCheckForCreateInviteCodeSegment: builder.mutation({
      query: (requestParams: BaseInviteCodeSegment) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'preCheckForCreateInviteCodeSegment',
          requestParams
        )
    }),
    createInviteCodeSegment: builder.mutation({
      query: (requestParams: BaseInviteCodeSegment) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'createInviteCodeSegment',
          requestParams
        )
    }),
    preCheckForEditInviteCodeSegment: builder.mutation({
      query: (requestParams: BaseInviteCodeSegment) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'preCheckForEditInviteCodeSegment',
          requestParams
        )
    }),
    deleteCodesFromInviteCodeSegment: builder.mutation({
      query: (requestParams: BaseInviteCodeSegment) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'deleteCodesFromInviteCodeSegment',
          requestParams
        ),
      invalidatesTags: ['TargetGroupDetails', 'TargetGroupAvailableCodes']
    }),
    editInviteCodeSegment: builder.mutation({
      query: (requestParams: BaseInviteCodeSegment) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'editInviteCodeSegment',
          requestParams
        ),
      invalidatesTags: ['TargetGroupDetails', 'TargetGroupAvailableCodes']
    }),
    addMoreCodesForInviteCodeSegment: builder.mutation({
      query: (requestParams: BaseInviteCodeSegment) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'addMoreCodesForInviteCodeSegment',
          requestParams
        ),
      invalidatesTags: ['TargetGroupDetails', 'TargetGroupAvailableCodes']
    }),
    replaceCodesForInviteCodeSegment: builder.mutation({
      query: (requestParams: BaseInviteCodeSegment) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'replaceCodesForInviteCodeSegment',
          requestParams
        ),
      invalidatesTags: ['TargetGroupDetails', 'TargetGroupAvailableCodes']
    }),
    validateMsbAccountIDsForTargetGroup: builder.mutation({
      query: (requestParams: ProcessMsbAccountIDParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'validateMsbAccountIDsForTargetGroup',
          requestParams
        )
    }),
    processMsbAccountIDsForTargetGroup: builder.mutation({
      query: (requestParams: ProcessMsbAccountIDParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'processMsbAccountIDsForTargetGroup',
          requestParams
        ),
      invalidatesTags: ['TargetGroupMsbFailureLogs']
    }),
    getPreSegmentAssociationFailureLogsByID: builder.query({
      query: (requestParams: PreSegmentAssociationFailureLogParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getPreSegmentAssociationFailureLogs',
          requestParams
        ),
      providesTags: ['TargetGroupMsbFailureLogs']
    }),
    downloadMsbFailureLogsFromTargetGroup: builder.mutation({
      query: (requestParams: CommonDownloadFromTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'downloadMsbFailureLogsFromTargetGroup',
          requestParams
        )
    }),
    downloadFailureLogsFromTargetGroup: builder.mutation({
      query: (requestParams: CommonDownloadFromTargetGroupParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'downloadFailureLogsFromTargetGroup',
          requestParams
        )
    }),
    getTargetGroupFailureLogsByID: builder.query({
      query: (requestParams: PreSegmentAssociationFailureLogParams) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getTargetGroupFailureLogsByID',
          requestParams
        ),
      providesTags: ['TargetGroupFailureLogs']
    })
  })
})

export const {
  useGetAllSegmentListQuery,
  useDeleteTargetGroupAvailableCodeMutation,
  useGetAllOffersFromGroupQuery,
  useGetExcludedCampaignsFromGroupQuery,
  useGetTargetGroupAvailableCodesQuery,
  useGetTargetGroupDetailsByIDQuery,
  useGetTargetGroupUpdateCardsByIDQuery,
  useGetTargetGroupsQuery,
  useLazyGetUsersInSegmentCountQuery,
  useLazyValidateManualTargetGroupQuery,
  useCreateManualTargetGroupMutation,
  useUpdateTargetGroupAndDeleteUsersMutation,
  useUpdateTargetGroupNameMutation,
  useUpdateTargetGroupAndAddUsersMutation,
  useUpdateTargetGroupAndReplaceUsersMutation,
  useDownloadUsersFromTargetGroupMutation,
  useDeleteTargetGroupMutation,
  useDownloadAvailableCodesFromTargetGroupMutation,
  useValidateInviteCodeSegmentStepsMutation,
  usePreCheckForCreateInviteCodeSegmentMutation,
  useCreateInviteCodeSegmentMutation,
  usePreCheckForEditInviteCodeSegmentMutation,
  useDeleteCodesFromInviteCodeSegmentMutation,
  useEditInviteCodeSegmentMutation,
  useAddMoreCodesForInviteCodeSegmentMutation,
  useReplaceCodesForInviteCodeSegmentMutation,
  useValidateMsbAccountIDsForTargetGroupMutation,
  useProcessMsbAccountIDsForTargetGroupMutation,
  useGetPreSegmentAssociationFailureLogsByIDQuery,
  useDownloadMsbFailureLogsFromTargetGroupMutation,
  useGetTargetGroupActionBoxesByIDQuery,
  useDownloadFailureLogsFromTargetGroupMutation,
  useGetTargetGroupFailureLogsByIDQuery
} = targetGroupsApi
